import { User } from '@/api/User';
import ErrorMessage from '@/components/ErrorMessage';
import StatusBadge from '@/components/StatusBadge';
import { AddUserIcon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/button';
import { OrgUtil } from '@/data/OrgUtil';
import { userService } from '@/service/UserService';
import { Box, Heading, HStack, IconButton, Spinner, Table, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LuPencil, LuUsers } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

interface PartnerExternUsersPanelProps {
    partnerId: number;
}

const PartnerExternUsersPanel = ({ partnerId }: PartnerExternUsersPanelProps) => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const loadUsers = useCallback((): Promise<void> => {
        setIsLoading(true);
        setError('');

        return userService.listAll()
            .then(response => {
                setUsers(response.results
                    .filter(user => OrgUtil.isRoleExtern(user.role) && user.partnerId === partnerId)
                    .sort((a, b) => `${a.lastName} ${a.firstName} ${a.email}`.localeCompare(
                        `${b.lastName} ${b.firstName} ${b.email}`,
                        undefined,
                        { sensitivity: 'base' }
                    )));
            })
            .catch(err => setError(err?.message ?? 'Partner Benutzer konnten nicht geladen werden.'))
            .finally(() => setIsLoading(false));
    }, [partnerId]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const addUserPath = useMemo(() => `/admin/users/add?partnerId=${partnerId}&source=partnerExternUsersPanel`, [partnerId]);

    return (
        <Box borderWidth='1px' borderColor='border.subtle' rounded='lg' bg='bg.panel' overflow='hidden'>
            <Box px='4' py='3' borderBottomWidth='1px' borderColor='border.subtle'>
                <HStack justify='space-between' gap='4' align='center' flexWrap='wrap'>
                    <HStack gap='2'>
                        <LuUsers />
                        <Heading as='h2' size='lg'>
                            Partner Benutzer
                        </Heading>
                    </HStack>
                    <Button colorPalette='blue' rounded='md' onClick={() => navigate(addUserPath)}>
                        <AddUserIcon /> Partner Anmeldung Erstellen
                    </Button>
                </HStack>
            </Box>

            {error && (
                <Box px='4'>
                    <ErrorMessage message={error} />
                </Box>
            )}

            <Box overflowX='auto'>
                <Table.Root size='sm' minW='42rem'>
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>Name</Table.ColumnHeader>
                            <Table.ColumnHeader>E-Mail</Table.ColumnHeader>
                            <Table.ColumnHeader>Status</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign='end'>Aktionen</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isLoading && (
                            <Table.Row>
                                <Table.Cell colSpan={4} py='8' textAlign='center'>
                                    <Spinner size='sm' mr='2' />
                                    <Text as='span' color='fg.muted'>Partner Benutzer werden geladen...</Text>
                                </Table.Cell>
                            </Table.Row>
                        )}
                        {!isLoading && users.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan={4} py='8' textAlign='center'>
                                    <Text color='fg.muted'>Keine Partner Benutzer vorhanden.</Text>
                                </Table.Cell>
                            </Table.Row>
                        )}
                        {!isLoading && users.map(user => (
                            <Table.Row
                                key={user.id}
                                cursor='pointer'
                                _hover={{ bg: 'blue.50' }}
                                onClick={() => navigate(`/admin/users/edit/${user.id}`)}
                            >
                                <Table.Cell fontWeight='medium'>{user.firstName} {user.lastName}</Table.Cell>
                                <Table.Cell>{user.email}</Table.Cell>
                                <Table.Cell><StatusBadge status={user.status} /></Table.Cell>
                                <Table.Cell textAlign='end'>
                                    <IconButton
                                        aria-label='Benutzer bearbeiten'
                                        size='xs'
                                        variant='ghost'
                                        onClick={event => {
                                            event.stopPropagation();
                                            navigate(`/admin/users/edit/${user.id}`);
                                        }}
                                    >
                                        <LuPencil />
                                    </IconButton>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </Box>
        </Box>
    );
};

export default PartnerExternUsersPanel;
